import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

type PlanTier = 'beta_free' | 'early_supporter';

interface PlanLimits {
  maxActiveClients: number;
}

interface PlanContextValue {
  planTier: PlanTier;
  limits: PlanLimits;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const PlanContext = createContext<PlanContextValue | undefined>(undefined);

export const PlanProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [planTier, setPlanTier] = useState<PlanTier>('beta_free');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlanData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setPlanTier('beta_free');
        return;
      }

      // Get max clients from Supabase function
      const { data: maxClients, error: maxError } = await supabase
        .rpc('get_trainer_max_clients', { p_trainer: user.id });

      if (maxError) {
        console.error('Error fetching max clients:', maxError);
        setError('Failed to load plan limits');
        return;
      }

      // Determine plan tier based on max clients
      if (maxClients >= 30) {
        setPlanTier('early_supporter');
      } else {
        setPlanTier('beta_free');
      }

    } catch (err) {
      console.error('Error fetching plan data:', err);
      setError('Failed to load plan data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanData();
  }, []);

  const limits: PlanLimits = useMemo(() => {
    if (planTier === 'early_supporter') {
      return { maxActiveClients: 30 };
    }
    return { maxActiveClients: 5 };
  }, [planTier]);

  const value = useMemo<PlanContextValue>(() => ({
    planTier,
    limits,
    loading,
    error,
    refresh: fetchPlanData,
  }), [planTier, limits, loading, error]);

  return (
    <PlanContext.Provider value={value}>{children}</PlanContext.Provider>
  );
};

export function usePlan() {
  const ctx = useContext(PlanContext);
  if (!ctx) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return ctx;
}

// Development-only hook for testing different plan states
export function usePlanDev() {
  const { planTier, loading, error, refresh, limits } = usePlan();
  const [devPlanTier, setDevPlanTier] = useState<PlanTier>(planTier);
  
  if (process.env.NODE_ENV !== 'development') {
    return { planTier, loading, error, refresh, limits };
  }

  return {
    planTier: devPlanTier,
    loading,
    error,
    refresh,
    limits: devPlanTier === 'early_supporter' ? { maxActiveClients: 30 } : { maxActiveClients: 5 },
    setPlanTierPreview: (tier: PlanTier) => {
      console.log('Dev override: setting plan tier to', tier);
      setDevPlanTier(tier);
    },
  };
}


